import { MapPin, Check, Phone } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useIsMobile } from '@/hooks/use-mobile';

const serviceAreas = [
  { name: "Highlands Ranch", isServed: true, link: "/highlands-ranch-plumber" },
  { name: "Lakewood", isServed: true, link: "/lakewood-plumber" },
  { name: "Centennial", isServed: true, link: "/centennial-plumber" },
  { name: "Arvada", isServed: true, link: "/arvada-plumber" },
  { name: "Thornton", isServed: true, link: "/thornton-plumber" },
  { name: "Denver", isServed: true, link: "/denver-plumber" },
  { name: "Castle Rock", isServed: true, link: "/castle-rock-plumber" },
  { name: "Longmont", isServed: true, link: "/longmont-plumber" },
  { name: "and surrounding areas", isServed: true, link: null }
];

const ServiceArea = () => {
  const isMobile = useIsMobile();

  return (
    <section id="service-area" className="py-16 md:py-20 bg-gradient-to-br from-blue-900 to-blue-950 relative">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
      
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-16 reveal-slide" style={{ '--reveal-delay': 0 } as React.CSSProperties}>
          <div className="inline-block px-4 py-2 mb-6 rounded-full bg-blue-100 text-blue-800 font-semibold text-sm border border-blue-200">
            🗺️ Where We Serve Colorado
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white">Our Service Areas</h2>
          <div className="text-left max-w-3xl mx-auto">
            <p className="text-xl md:text-2xl font-bold text-yellow-300 mb-3">
              What areas does Colorado First Plumbing and Sewer LLC serve?
            </p>
            <p className="text-lg md:text-xl text-blue-200 leading-relaxed">
              We are located in Littleton, Colorado, and proudly service Highlands Ranch, Lakewood, Centennial, Arvada, Thornton, Denver, Castle Rock, Longmont, and most other Denver Metro Areas. We are the Denver Plumber that offers transparent, flat-rate pricing for plumbing services, including water heater and sewer replacements, in all these locations.
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start mb-12">
          {/* Service Areas List - Now Featured and Prominent */}
          <div className="order-1 reveal-slide" style={{ '--reveal-delay': 1 } as React.CSSProperties}>
            <div className="group bg-gradient-to-br from-trust-deep to-authority-navy rounded-2xl shadow-2xl hover:shadow-3xl transition-all duration-300 overflow-hidden">
              <div className="h-3 bg-gradient-to-r from-success-primary to-action-primary"></div>
              <div className="p-8 text-white">
                <h3 className="text-2xl font-bold mb-6 flex items-center">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mr-4 border border-white/30 shadow-lg">
                    <MapPin size={28} className="text-success-primary" />
                  </div>
                  Areas We Proudly Serve
                </h3>
                
                <div className="space-y-2 mb-8">
                  {serviceAreas.map((area, index) => (
                    <div key={index} className="text-lg">
                      {area.link ? (
                        <a 
                          href={area.link} 
                          className="text-blue-300 hover:text-blue-400 underline font-medium"
                          title={`Visit our ${area.name} plumber page`}
                        >
                          {area.name}
                        </a>
                      ) : (
                        <span className="text-white/90 font-medium">{area.name}</span>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Emergency Contact Highlight */}
                <div className="bg-gradient-to-r from-action-urgent to-red-700 rounded-xl p-6 shadow-lg border-l-4 border-red-500">
                  <h4 className="font-bold text-lg mb-2 flex items-center text-white">
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mr-3">
                      <Phone size={18} className="text-white" />
                    </div>
                    Emergency Service Available (8 AM - 5 PM Mon-Sat)
                  </h4>
                  
                  {/* Emergency Service Information */}
                  <div className="bg-white/15 rounded-lg p-3 mb-4 border border-white/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-bold">Emergency Service Available:</span>
                      <span className="text-yellow-200 font-bold text-lg">Same-Day</span>
                    </div>
                    <p className="text-red-100 text-sm leading-relaxed">
                      <strong>Priority emergency service:</strong> Emergency calls receive priority scheduling with same-day response when possible. 
                      We redirect our technicians from scheduled jobs to prioritize urgent plumbing needs during business hours.
                    </p>
                  </div>
                  
                  <p className="text-red-100 mb-4 font-medium">
                    Same-day response when possible • Business hours coverage • Licensed & Insured • 4.8★ Rating
                  </p>
                  
                </div>
              </div>
            </div>
          </div>

          {/* Map Section with Contact Card Below */}
          <div className="order-2 space-y-8 reveal-slide" style={{ '--reveal-delay': 2 } as React.CSSProperties}>
            {/* Map */}
            <div className="group bg-gradient-to-br from-success-primary to-trust-primary rounded-2xl shadow-2xl hover:shadow-3xl transition-all duration-300 overflow-hidden">
              <div className="h-3 bg-gradient-to-r from-action-primary to-success-eco"></div>
              
              {/* Google Maps Embed */}
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d973513.8144041519!2d-104.92004839999998!3d39.88847755!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x876c880ce18fffff%3A0x9ab52b1e46a6d9b1!2sColorado%20First%20Plumbing%20and%20Sewer%20LLC!5e1!3m2!1sen!2sus!4v1742570693105!5m2!1sen!2sus" 
                width="100%" 
                height="400" 
                style={{ border: 0 }} 
                allowFullScreen 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Colorado First Plumbing and Sewer LLC Google Maps Location"
                className="w-full aspect-[16/9]"
              ></iframe>
            </div>

            {/* Contact Confirmation Card - Moved directly below map */}
            <div className="bg-gradient-to-br from-success-primary to-success-eco rounded-2xl shadow-2xl text-white overflow-hidden">
              <div className="h-3 bg-gradient-to-r from-success-eco to-trust-primary"></div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center mr-3 border border-white/30">
                    <MapPin size={20} className="text-success-light" />
                  </div>
                  Not Sure If We Serve Your Area?
                </h3>
                <p className="text-success-light mb-6 font-medium">
                  If you don't see your location listed, give us a call! We're expanding our service areas and may be able to accommodate your plumbing needs.
                </p>
                <div className="space-y-3">
                  
                  <div className="flex items-center justify-center space-x-4 text-success-light text-sm">
                    <div className="flex items-center space-x-1">
                      <div className="w-4 h-4 bg-white rounded-full flex items-center justify-center">
                        <span className="text-success-primary text-xs">✓</span>
                      </div>
                      <span>Licensed & Insured</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-4 h-4 bg-white rounded-full flex items-center justify-center">
                        <span className="text-success-primary text-xs">✓</span>
                      </div>
                      <span>Free Estimates</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA Section - Enhanced */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-trust-deep to-authority-navy rounded-2xl p-8 md:p-12 text-white shadow-2xl border border-trust-primary/20 reveal-slide" style={{ '--reveal-delay': 5 } as React.CSSProperties}>
            <div className="max-w-4xl mx-auto">
              <h3 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Serve Your Colorado Property
              </h3>
              <p className="text-xl mb-8 text-trust-light">
                Professional plumbing experts with emergency service available during business hours (8 AM - 5 PM Mon-Sat) • Master plumber owned • 4.8★ rating • 300+ sewer projects completed
              </p>
              
              {/* Main CTAs */}
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
                
                <a 
                  href="/pricing" 
                  className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-10 py-5 rounded-full text-xl font-semibold inline-block transition-all duration-300 w-full sm:w-auto shadow-lg"
                >
                  💰 View Transparent Pricing
                </a>
              </div>
              
              {/* Enhanced Trust Signals */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl mx-auto">
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-3 border border-white/30">
                    <Check size={24} className="text-success-primary" />
                  </div>
                  <h4 className="font-bold text-white mb-1">Licensed & Insured</h4>
                  <p className="text-trust-light text-sm">Master Plumber Owned</p>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-3 border border-white/30">
                    <Phone size={24} className="text-action-primary" />
                  </div>
                  <h4 className="font-bold text-white mb-1">Emergency Service Available</h4>
                  <p className="text-trust-light text-sm">8 AM - 5 PM Mon-Sat</p>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-3 border border-white/30">
                    <MapPin size={24} className="text-success-eco" />
                  </div>
                  <h4 className="font-bold text-white mb-1">Transparent Pricing</h4>
                  <p className="text-trust-light text-sm">No Hidden Fees</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceArea;
